import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, TextField, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLibraryReports } from '../../slices/librarySlice';
import { C, FONT, inputSx, primaryButtonSx, Panel, StatTile, TileGrid, BarList } from './libraryUi';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import AlarmIcon from '@mui/icons-material/AlarmOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';

const LibraryReportsTab = () => {
  const dispatch = useDispatch();
  const { reports, loading, error } = useSelector((state) => state.library);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    dispatch(fetchLibraryReports());
  }, [dispatch]);

  const handleGenerateReport = () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    dispatch(fetchLibraryReports(params));
  };

  const handleClear = () => {
    setDateFrom('');
    setDateTo('');
    dispatch(fetchLibraryReports());
  };

  if (loading && !reports) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress sx={{ color: C.primary }} />
      </Box>
    );
  }

  if (error && !reports) {
    return <Alert severity="error" sx={{ borderRadius: 3 }}>{error.error || 'حدث خطأ في تحميل التقرير'}</Alert>;
  }

  const categoryRows = (reports?.items_by_category || []).map((row) => ({
    label: row.category || 'بدون تصنيف',
    value: row.count,
  }));
  const typeRows = (reports?.items_by_type || []).map((row) => ({
    label: row.label || row.item_type,
    value: row.count,
  }));

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Panel>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <Typography fontWeight={800} color={C.text} sx={{ fontFamily: FONT, ml: 1 }}>
            الفترة
          </Typography>
          <TextField
            label="من"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
          <TextField
            label="إلى"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
          <Button variant="contained" onClick={handleGenerateReport} sx={primaryButtonSx}>
            عرض
          </Button>
          {(dateFrom || dateTo) && (
            <Button onClick={handleClear} sx={{ fontFamily: FONT, color: C.muted }}>
              مسح
            </Button>
          )}
        </Box>
      </Panel>

      {reports && (
        <>
          <TileGrid min={200}>
            <StatTile label="استعارات" value={reports.total_borrows || 0} icon={<SwapHorizIcon />} />
            <StatTile label="إرجاعات" value={reports.total_returns || 0} icon={<KeyboardReturnIcon />} tone="success" />
            <StatTile
              label="متأخرة حالياً"
              value={reports.total_overdue || 0}
              icon={<AlarmIcon />}
              tone={reports.total_overdue ? 'danger' : 'neutral'}
            />
            <StatTile label="الغرامات" value={reports.total_fines || 0} icon={<PaymentsIcon />} tone="warning" />
          </TileGrid>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, alignItems: 'start' }}>
            <Panel title="الكتب حسب التصنيف">
              {categoryRows.length ? <BarList rows={categoryRows} /> : (
                <Typography color={C.muted} textAlign="center" py={2} sx={{ fontFamily: FONT }}>لا توجد بيانات</Typography>
              )}
            </Panel>
            <Panel title="الكتب حسب النوع">
              {typeRows.length ? <BarList rows={typeRows} /> : (
                <Typography color={C.muted} textAlign="center" py={2} sx={{ fontFamily: FONT }}>لا توجد بيانات</Typography>
              )}
            </Panel>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LibraryReportsTab;
